/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Feature, FeatureCollection, Geometry, MultiPolygon, Polygon, Position } from 'geojson';

// Basic UTM (WGS84) to lon/lat conversion without external deps.
// Assumes northern hemisphere; sufficient for Oman zones (UTM 39N/40N).
const WGS84_A = 6378137.0; // semi-major axis
const WGS84_E = 0.08181919084262149; // eccentricity
const K0 = 0.9996; // scale factor

function radToDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

function utmToLonLat(easting: number, northing: number, zone: number, northernHemisphere = true): [number, number] {
  const e = WGS84_E;
  const e1sq = (e * e) / (1 - e * e);

  const x = easting - 500000.0; // remove 500,000 meter offset for longitude
  let y = northing;
  if (!northernHemisphere) y -= 10000000.0; // not used for Oman

  const m = y / K0;
  const mu = m / (WGS84_A * (1 - Math.pow(e, 2) / 4 - (3 * Math.pow(e, 4)) / 64 - (5 * Math.pow(e, 6)) / 256));

  const e1 = (1 - Math.sqrt(1 - e * e)) / (1 + Math.sqrt(1 - e * e));
  const j1 = (3 * e1) / 2 - (27 * Math.pow(e1, 3)) / 32;
  const j2 = (21 * Math.pow(e1, 2)) / 16 - (55 * Math.pow(e1, 4)) / 32;
  const j3 = (151 * Math.pow(e1, 3)) / 96;
  const j4 = (1097 * Math.pow(e1, 4)) / 512;

  const fp = mu + j1 * Math.sin(2 * mu) + j2 * Math.sin(4 * mu) + j3 * Math.sin(6 * mu) + j4 * Math.sin(8 * mu);

  const c1 = e1sq * Math.pow(Math.cos(fp), 2);
  const t1 = Math.pow(Math.tan(fp), 2);
  const r1 = (WGS84_A * (1 - e * e)) / Math.pow(1 - Math.pow(e * Math.sin(fp), 2), 1.5);
  const n1 = WGS84_A / Math.sqrt(1 - Math.pow(e * Math.sin(fp), 2));
  const d = x / (n1 * K0);

  // latitude
  const lat =
    fp -
    ((n1 * Math.tan(fp)) / r1) *
      (Math.pow(d, 2) / 2 -
        ((5 + 3 * t1 + 10 * c1 - 4 * Math.pow(c1, 2) - 9 * e1sq) * Math.pow(d, 4)) / 24 +
        ((61 + 90 * t1 + 298 * c1 + 45 * Math.pow(t1, 2) - 252 * e1sq - 3 * Math.pow(c1, 2)) * Math.pow(d, 6)) / 720);

  // longitude
  const lonOrigin = (zone - 1) * 6 - 180 + 3;
  const lon =
    (d -
      ((1 + 2 * t1 + c1) * Math.pow(d, 3)) / 6 +
      ((5 - 2 * c1 + 28 * t1 - 3 * Math.pow(c1, 2) + 8 * e1sq + 24 * Math.pow(t1, 2)) * Math.pow(d, 5)) / 120) /
      Math.cos(fp) +
    lonOrigin * (Math.PI / 180);

  return [radToDeg(lon), radToDeg(lat)];
}

function parseUtmZoneFromCrsName(name?: string): { zone: number; northern: boolean } | null {
  if (!name) return null;
  // Expect formats like "urn:ogc:def:crs:EPSG::32639" where 32639 => UTM 39N, northern hemisphere
  const match = name.match(/EPSG::(\d{5})/);
  if (!match) return null;
  const code = Number(match[1]);
  if (code >= 32601 && code <= 32660) {
    return { zone: code - 32600, northern: true };
  }
  if (code >= 32701 && code <= 32760) {
    return { zone: code - 32700, northern: false };
  }
  return null;
}

function reprojectPosition(pos: Position, zone: number, northern: boolean): Position {
  const [x, y, z] = pos as [number, number, number?];
  const [lon, lat] = utmToLonLat(x, y, zone, northern);
  return z != null ? [lon, lat, z] : [lon, lat];
}

function reprojectPolygon(coords: Position[][], zone: number, northern: boolean): Position[][] {
  return coords.map((ring) => ring.map((p) => reprojectPosition(p, zone, northern)));
}

function reprojectMultiPolygon(coords: Position[][][], zone: number, northern: boolean): Position[][][] {
  return coords.map((poly) => reprojectPolygon(poly, zone, northern));
}

export function reprojectFeatureCollectionToWgs84<T extends Geometry = Geometry>(
  fc: FeatureCollection<T>,
): FeatureCollection<T> {
  const crsName = (fc as any)?.crs?.properties?.name as string | undefined;
  const utm = parseUtmZoneFromCrsName(crsName);
  if (!utm) return fc;

  const features = (fc.features as Feature<T>[]).map((feat) => {
    const g = feat.geometry as Geometry;
    if (!g) return feat;
    if (g.type === 'Polygon') {
      const coords = reprojectPolygon((g as Polygon).coordinates, utm.zone, utm.northern);
      return { ...feat, geometry: { type: 'Polygon', coordinates: coords } as any };
    }
    if (g.type === 'MultiPolygon') {
      const coords = reprojectMultiPolygon((g as MultiPolygon).coordinates, utm.zone, utm.northern);
      return { ...feat, geometry: { type: 'MultiPolygon', coordinates: coords } as any };
    }
    // other geometry types not expected for plots; return unchanged
    return feat;
  });

  // Return a shallow copy without the CRS (Leaflet expects WGS84)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { crs, ...rest } = fc as any;
  return {
    ...(rest as FeatureCollection<T>),
    type: 'FeatureCollection',
    features: features as any,
  } as FeatureCollection<T>;
}
